import { Clipboard } from '@angular/cdk/clipboard';
import { AfterViewInit, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, TemplateRef, ViewChild, inject } from '@angular/core';
import { FormArray, FormBuilder, FormControl } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { catchError, distinctUntilChanged, filter, map, pairwise, take, takeUntil, tap } from 'rxjs/operators';
import { AuthService } from 'src/app/core/services/auth.service';
import { FormObject, ITrolyFormField, ITrolyLoadingStatus, ITrolySocketNotification } from '../models/form_objects';
import { TrolyService } from '../services/troly/troly.service';

import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { Observable, combineLatest, of } from 'rxjs';
import { FormStatusComponent } from 'src/app/shared/widgets/forms-status/form-status.component';
import { RecentService } from 'src/app/shared/widgets/recent/recent.service';
import { SweetAlertOptions, SweetAlertPosition } from 'sweetalert2';
import { I18nLoader } from '../i18n_loader';
import { TrolyFormGroup } from '../models/troly_form';
import { TrolyObject } from '../models/troly_object';
import { uuid } from '../models/utils.models';
import { CompanyService } from '../services/troly/company.service';
import { UserService } from '../services/troly/user.service';
import { WebsocketService } from '../services/troly/websocket.service';
import { AlertService } from '../services/utils/alert.service';
import { BaseComponent, compareHashMap } from './base.component';

@Component({
	selector: 'troly-component',
	template: '',
}) // @Component and @Directive include @Injectable
export abstract class TrolyComponent extends BaseComponent implements OnChanges, OnInit, AfterViewInit, OnDestroy {

	override readonly __name:string = 'TrolyComponent';

	protected fb: FormBuilder;

	public moment: any = moment; // makes the moment library available within the .html template file for the component.

	public readonly dateNow = moment();
	public datetimeShort = "D MMM. H:mm";           // defaults to british standard
	public datetimeLong = "D MMMM, H:mm:ss";
	public datetimeFull = "MMM D MMMM YY, H:mm:ss";
	public dateShort = "D MMM. \'YY";
	public dateFull = "D MMMM Y";

	public __fromNow: boolean = true; // used to display timestamps as exact (dd MM YY) or relative time  (3 days ago)
	public readonly bsCssClassPrefix:string = '';

	@Input() record?: TrolyObject; // the primary record currently being monitored by a component
	declare selectedRecord?: FormObject; // used to "select" and pass a record to a modal window -- will not be overriden when the modal is open, but some attribute may be changed or added

	public routeParams: Params;
	public pageFragment: string; // at all times, the current page fragment

	/**
	 * Loading indicators for various action or attributes.
	 * When Overwriting the attribute in any component, only 'undefined' attributes will be automatically marked as 'loading' via onInit()
	 */
	public loading: { record: ITrolyLoadingStatus, troly: ITrolyLoadingStatus} = { record:undefined, troly:undefined }; /* holds the loading class indicators, slow internet is indicated by loading-slow by */

	/**
	 * The currently authenticated user as advertised by `@this.userService` and subscribed to by `@this.subscribeRecord`.
	 * @see TrolyComponent.authenticatedUser (Override)
	 */
	@ViewChild(FormStatusComponent) formStatus?: FormStatusComponent;

	protected authService: AuthService = inject(AuthService);
	protected titleService: Title = inject(Title);
	protected translateService: TranslateService = inject(TranslateService);
	
	protected router: Router = inject(Router);
	protected route: ActivatedRoute = inject(ActivatedRoute);

	protected userService: UserService = inject(UserService);
	protected companyService: CompanyService = inject(CompanyService);

	protected alertService: AlertService = inject(AlertService);

	constructor() {

		super()

		this.fb = new FormBuilder(); // required to ensure

		//const companyUpdate = this.companyService.record$.pipe(takeUntil(this.observablesDestroy$),filter(_ => !this.selectedCompany?.id || _.id == this.companyService.record$.value?.id)) // note we are able to change company "live"
		const companyUpdate = this.companyService.record$.pipe(takeUntil(this.observablesDestroy$),filter(_ => !this.selectedCompany?.id || _.sameAs(this.selectedCompany))) // EXCEPT
		const userUpdate = this.userService.record$.pipe(takeUntil(this.observablesDestroy$),filter(_ => !this.authenticatedUser?.id || _.sameAs(this.authenticatedUser)))

		companyUpdate.subscribe((_) => { this.selectedCompany = _; });
		userUpdate.subscribe((_) => { this.authenticatedUser = _; });

		combineLatest([ companyUpdate, userUpdate ])
			.pipe(take(1))
			.subscribe(([user, company]) => {
				this.markAsLoaded('troly');

				// Note about using authenticatedUser and selectedCompany:
				// They are normally loaded pretty early on but there's no
				// guarantee (due to Observable subscription) that they are loaded by the time
				// the contructor exists.
				// This is why we should use `.loadData()` for anything needing them.
				// The constructor of any component should be to initialise the
				// components and variables, not data.

			});

		//
		// set the currently selected card based on route fragment requested
		//
		this.route.fragment.subscribe((_) => this.pageFragment = _);

		if ((new Date()).toLocaleTimeString().endsWith('M')) {
			// for american-style date formats as configured in the browser, we change the default formats
			this.datetimeShort = "MMM. D, hh:mm a";
			this.datetimeFull = "dddd MMMM D, Y, hh:mm:ss a";
			this.datetimeLong = "dddd MMMM D, hh:mm:ss a";
			this.dateShort = "MMM. D, 'YY";
			this.dateFull = " MMMM D, Y";
		}

		this.localCache.attachStorageNotifier<boolean>(`${this.__name}.date-is-relative.persist`).pipe(takeUntil(this.observablesDestroy$)).subscribe(_ => this.__fromNow = _);

	}

	//  .d8888b.  888                           888                      888       .d8888b.           888 888 888                        888
	// d88P  Y88b 888                           888                      888      d88P  Y88b          888 888 888                        888
	// Y88b.      888                           888                      888      888    888          888 888 888                        888
	//  "Y888b.   888888  8888b.  88888b.   .d88888  8888b.  888d888 .d88888      888         8888b.  888 888 88888b.   8888b.   .d8888b 888  888 .d8888b
	//     "Y88b. 888        "88b 888 "88b d88" 888     "88b 888P"  d88" 888      888            "88b 888 888 888 "88b     "88b d88P"    888 .88P 88K
	//       "888 888    .d888888 888  888 888  888 .d888888 888    888  888      888    888 .d888888 888 888 888  888 .d888888 888      888888K  "Y8888b.
	// Y88b  d88P Y88b.  888  888 888  888 Y88b 888 888  888 888    Y88b 888      Y88b  d88P 888  888 888 888 888 d88P 888  888 Y88b.    888 "88b      X88
	//  "Y8888P"   "Y888 "Y888888 888  888  "Y88888 "Y888888 888     "Y88888       "Y8888P"  "Y888888 888 888 88888P"  "Y888888  "Y8888P 888  888  88888P'


	/**
	 * Used to ensure loading indicators and class variables are correctly reinitialised when a navigation event occurs.
	 * @param oldId
	 * @param newId
	 * @returns
	 */
	protected _resetOnNavigate(oldId: uuid, newId: uuid): boolean {

		if ((oldId != newId)  // browsing to a new record
			|| (oldId.toString() == '' && newId.toString() == '')) // initializing (we don't have a record loaded yet)
		{
			return true;
		} else if (oldId.toString() != '' && newId.toString() != '') {
			this.onDestroy()
		}
		return false;
	}

	/**
	 * The best place to init variables and build forms.
	 * This method must be overrident in the component itself.
	 * @param oldId
	 * @param newId
	 */
	protected resetOnNavigate(oldId: uuid, newId: uuid): void {
		this._resetOnNavigate(oldId, newId);
	}

	protected onDestroy(): void {
		this.record = null;
		this.selectedRecord=null;
	}

	/**
	 * When changing page (and tabs) the recentService allows to save the records visited 'recently.
	 */
	protected recentService: RecentService = inject(RecentService);


	//public selectedRecord?: Object; // used to "select" and pass a record to a modal window -- will not be overriden when the modal is open, but some attribute may be changed or added
	public openModal(template:TemplateRef<any>, vars?: TrolyObject|{}, options?: any, clearPrevious:boolean=true): boolean {

		// When opening a modal we need to forcefully reset the placeholder (shared with all modals)
		// This is a workaround to pass parameters DYNAMICALLY to the modal component in the template..
		// Assumes the [record] attribute of the modal was set to `selectedRecord`
		const previousSelectedRecordValue = this.selectedRecord;
		if (vars) {
			this.selectedRecord = (vars instanceof TrolyObject) ? vars : this.service.make(vars);
		}

		if (super.openModal(template, vars, options, clearPrevious)) {
			
			if (vars) {
				this.windowService.currentModal.result.finally(() => {
					this.selectedRecord = previousSelectedRecordValue;
				});
			}

			return true;

		}
		return false;
	}

	/**
	 * The best place to attach 'changes' hooks such as `valueChanges` and `statusChanges`.
	 * This method must be overrident in the component itself.
	 */
	protected attachChangeNotifiers(): void {
	}

	protected NO_EMIT = { emitEvent: false };

	/**
	 * The best place to attach data loading (subscribes) and assign to forms.
	 * This method must be overriden in the component itself.
	 */
	protected loadData(): void {
	}

	ngOnInit(): void {

		// NG15 type declarations -- we hadded initialisers to loading and setting them as loading indicate the 'initRecordAttributeOnce' doesn't need to run, which is incorrect
		//this.markAsLoading(); // sets all loading attributes as "loading" this cannot be in the constructor as all values (defined in extended classes) do not yet exist

		//let db = JSON.parse(window.localStorage.getItem('ui')) || {}
		//this.__fromNow = (Object.keys(db).includes('date.is-relative.persist') ? eval(db['date.is-relative.persist'].value) : this.__fromNow);

		// this is called initially (on init) to begin loading data (0, 0), AND also
		// when a record was requested via routing (0,###), or (###,###)
		// eg integration.modal.ts needs to fetch some data required for rendering the popup (so 0,0) is important
		//this.resetOnNavigate(0, 0); -- data should be loaded via loadData, resetOnNavigate is exclusively for routing//records access

		if (this.__path) { this.loadTranslation(this.__path, this.__name); }
		//
		// handles navigation + changes with records resetting/loading, this doesn't handle loading of data, instead triggers
		// resetOnNavigate()
		//
		//this.routeParams = this.route.snapshot.params;
		//this.router.events.pipe(filter((event: any) => event instanceof NavigationEnd)).subscribe((event) => {
		this.route.params.pipe(distinctUntilChanged(compareHashMap), takeUntil(this.observablesDestroy$)).subscribe((event) => {

			this.routeParams = this.route.snapshot.params;
			this.logTrace(`${this.__name}.constructor() via TrolyComponent received a new routeParams`);
			
			// we only notify of a change (via resetOnNavigate) when there was a record loaded and it's now changed.
			// if no record was loaded, then the component will make sure a default record is loaded as required.
			if (this.routeParams['id'] && this.record?.id != this.routeParams['id']) {
				// when we are initially loading a record
				this.resetOnNavigate(this.record?.id, new uuid(this.routeParams['id']));
			} 
		});

		this.loadData();

		// Angular doesn't allow instantly setting disable or enable
		// on reactive forms, as a workaround we must detect changes manually
		// See: https://github.com/angular/angular/issues/22556#issuecomment-501670796
		//this.changeDetector.detectChanges();

		if (this.route.snapshot.fragment) {
			// inspired by https://stackoverflow.com/questions/60562694/angular-9-scroll-restoration-anchor-scrolling-does-not-work-with-async-data-loa
			this.scrollIntoView(this.route.snapshot.fragment)
		}
	}

	protected afterViewInit(): void {}
	ngAfterViewInit(): void { this.afterViewInit(); }

	/**
	 * When input parameters are received, this handles the `TrolyComponent`-specific (and generic) tasks needed with handling of input data.
	 * Namely, if a form was initialised it will be patched, and if a `record` was received it will be marked as loaded.
	 */
	protected receiveInputParams(changes: SimpleChanges): void {
		if (this.form && changes.record?.currentValue) {
			this.markAsLoaded('record');

			this.updateForm();
			this.markAsLoaded(this.form);
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		this.receiveInputParams(changes)
	}

	ngOnDestroy(): void {
		super.ngOnDestroy()
		this.onDestroy()
	}


	//  .d8888b.                                                                      888         888     888 888    d8b 888 d8b 888    d8b
	// d88P  Y88b                                                                     888         888     888 888    Y8P 888 Y8P 888    Y8P
	// 888    888                                                                     888         888     888 888        888     888
	// 888         .d88b.  88888b.d88b.  88888b.   .d88b.  88888b.   .d88b.  88888b.  888888      888     888 888888 888 888 888 888888 888  .d88b.  .d8888b
	// 888        d88""88b 888 "888 "88b 888 "88b d88""88b 888 "88b d8P  Y8b 888 "88b 888         888     888 888    888 888 888 888    888 d8P  Y8b 88K
	// 888    888 888  888 888  888  888 888  888 888  888 888  888 88888888 888  888 888         888     888 888    888 888 888 888    888 88888888 "Y8888b.
	// Y88b  d88P Y88..88P 888  888  888 888 d88P Y88..88P 888  888 Y8b.     888  888 Y88b.       Y88b. .d88P Y88b.  888 888 888 Y88b.  888 Y8b.          X88
	//  "Y8888P"   "Y88P"  888  888  888 88888P"   "Y88P"  888  888  "Y8888  888  888  "Y888       "Y88888P"   "Y888 888 888 888  "Y888 888  "Y8888   88888P'
	//                                   888
	//                                   888
	//                                   888

	protected loadTranslation(path:string, nodeName:string=null) {
		if (path) {
			// defining a path means we dynamically load the translation for the component.
			(this.translateService.currentLoader as I18nLoader).getTranslation(this.translateService.currentLang, path, nodeName).pipe(take(1)).subscribe(_ => {
				this.translateService.setTranslation(this.translateService.currentLang, _);
			});
		}
	}

	/**
	 * Attaches a given socket observer to automatically update the primary record currently being managed.
	 * @param observer
	 */
	public attachRecordSocket(ws: WebsocketService, service?: TrolyService<any>) {
		service = service || this.service
		
		ws.connectCompanyChannel(this.selectedCompany.id)
		.subscribe((_event:ITrolySocketNotification) => {
			if (_event.operation == 'update') {
				if (this.record && this.record._trolyModelName == _event.model) {
					service._next(service.make(_event.data));
				}
			}
		});
	}

	/**
	 * Allows to update the current routing and add new queryParams based on any filtering required.
	 * This function is overriden in interfaces where filtering is not route based.
	 * @param params a key-value hash containing all changes to apply / ensure they are applied.
	 * @returns true if the route was changed, false if it was not.
	 */
	public setRoutingParams(params?: {}): void {
		
		params ||= {}
		
		if (Object.keys(params).length == 0) { 
			this.router.navigate(['.'], { relativeTo: this.route, queryParams: params, fragment: null });
		} else {
			// if unknown params exist in routing
			if (Object.keys(params).filter(_ => this.route.snapshot.queryParams[_] != params[_]).length > 0) {
				this.router.navigate(['.'], { relativeTo: this.route, queryParams: params, queryParamsHandling: 'merge' });
			}
		}
	}

	/**
	 * Subscribes to a service record observable (record$, changed only by `._find()`), will ;
	 * - automatically updates `this.record` 
	 * - patches the component `this.form` if one was created, 
	 * - marks the 'record' loading indicator `loading.record` as loaded 
	 * 
	 * This uses the default component service if none is provided.
	 * This method will safely unsubscribe when the component is destroyed.
	 * ?? I don't believe override_id is used anywhere. not usedful? should be deleted.
	 * @param service:TrolyService<TrolyOject> 
	 * @param override_id:uuid 
	 * @returns 
	 */
	protected subscribeRecord<T extends TrolyObject>(service?: TrolyService<T>, enforce_routing_param?: string): Observable<T> {

		service = service || this.service;

		// reset the record we are setting when the component is destroyed
		this.observablesDestroy$.subscribe(_ => { this.record = null; })

		return service.record$.pipe(
			takeUntil(this.observablesDestroy$),												// cleanly unsubscribe when the component is destroyed
			filter(_ => !!_),																			// avoid any initial null values emitted when setting up a BehaviorSubject
			filter(_ => !enforce_routing_param || this.route.snapshot.params[enforce_routing_param] == _.id),
			//filter(_ => override_id ? _.id == override_id : true),						// if we are overriding the id, we only want to receive the record with the id we are looking for
			//tap((_: T) => { if (!_.sameAs(this.record)) { this.observablesDestroy$.next(true) } }),
			map((_: T) => {

				this.logTrace(`${this.__name}.subscribeRecord via TrolyComponent: loaded record ${_['_trolyModelName']}:${_['id']}`);

				this.record = _ as T;

				this.markAsLoaded('record')

				if (this.form) {
					this.updateForm()
					if (_.isReadOnly()) { this.form.disable() }
					Object.keys(this.record._trolyPropertyArray).filter((_) => this.record._trolyPropertyArray[_] == Array && this.record[_]).map((prop) => {
						// For each attributes of type array present in the current record patch as Array,
						// patchValueArray() will not patch if the field is not present in the form.
						this.form.patchValue({[prop]:this.record[prop]});
					})
					this.markAsLoaded(this.form)
				}
				return this.record as T; // we've already assigned the record for convenience, this makes it easy on devs to use record or the value emitted.
			})
		);
	}

	/**
	 * This is a less 'noisy' version of `subscribeRecord` which allows to update the component main `.record` variable and update the form, however, this method will only emit when the record is different from the previous one.
	 * @param service 
	 * @returns 
	 */
	protected subscribeRecordDistinct<T extends TrolyObject>(service?: TrolyService<T>): Observable<T> {
		return this.subscribeRecord(service).pipe(
			distinctUntilChanged((a:T, b:T) => a.sameAs(b))		// Only received different records (ie, not the same as the previous one)
		);
	}

	/**
	 * This is a safe observable which will automatically terminates when the component is destroyed.
	 * @param service 
	 * @returns 
	 */
	protected subscribeDistinct<T extends TrolyObject>(service?: TrolyService<T>): Observable<T> {
		return (service || this.service).recordDistinct$.pipe(
			takeUntil(this.observablesDestroy$)													// cleanly unsubscribe when the component is destroyed
		);
	}

	/**
	 *
	 * @param what
	 * @param observable
	 * @param force
	 * @param dissipate
	 * @returns
	 */
	protected initRecordAttributeOnce<T>(what, attrObservable: Observable<T>, record?: TrolyObject | TrolyComponent): Observable<T> {

		record ||= this.record
		
		const force = record instanceof TrolyObject ? record.isStale(what) : false

		if ((force || !record[what]) && !this.isLoading(what)) {

			this.markAsLoading(what)

			// Here we are subscribing to and wrapping around the observable in order to add manipulations and error handling
			// but also, prevent the caller from having to subscribe too
			attrObservable.pipe(filter(_ => !!_)).subscribe({ // note, we are subscribing to more than 1, so that if updates are received, the record is updated too -- this is related to __reset_please
				next: (_: T) => {
					record[what] = _; // this doesn't directly notifies all components because we can use this function to load a component attribute
					this.markAsLoaded(what);
				},
				error: (err) => {
					this.logError(`${this.__name} via TrolyComponent.initRecordAttributeOnce(${what}): error (${err.toString()})`)
					this.markAsLoadingError(what, err);
				},
			});

		} else if (!record[what]) {
			this.logInfo(`${this.__name} via TrolyComponent.initRecordAttributeOnce(${what}): loading ${what} in OTHER SUBSCRIBE`)
		} else {
			this.logInfo(`${this.__name} via TrolyComponent.initRecordAttributeOnce(${what}): NOT loading ${what}`)

			if (this.loading[what] === undefined) {
				// if we never actually marked this filed as loading, it means that the attribute was already set. THEN we can return the set value
				this.markAsLoaded(what);
				// otherwise, if it's already marked as 'loading', it means it's likely loading in a diffent initRecordAttributeOnce call within the same subscribeRecord.. "loop".
				// This also prevents us having to pipe(take(1)) given that only 1 of the call (when loading was undefined, and we have defined it) will return a value OR it was undefined by a value was available.
				// in all other cases (See below -- we return nothing)
				return of(record[what])
			} else {
				this.markAsLoaded(what);
			}
		}

		// arch: the generic.service.ts does handle NOT calling the API several times for the same request (ie we can loadWarehouses in various components at the same time, returning the same observable to all)
		// however, we can still still need to handle the case where we are loading the same attribute in the same component, but from different places (ie, a component and a modal window)
		return attrObservable.pipe(filter(_ => !!_),take(1));
	}


	/** Forms Management HElper methods */
	public form: TrolyFormGroup;
	protected _formDefaults: {} = {};
	protected _formFields: ITrolyFormField[] = [];

	/**
	 * Initialises the main component form using the record, _formDefaults and _formFields
	 */
	protected initForm(attachChangeNotifier?: boolean, loadingComplete: boolean = true, updateOn?: 'blur' | 'change') {

		// the "standard" change notifier is attached as onblur to onFieldUpdate
		// whereas the "modal" windows don't "live load" and save on blur, but instead on submit.
		attachChangeNotifier = (attachChangeNotifier == undefined ? true : attachChangeNotifier)

		if (this._formFields.length == 0) {
			this.form = new TrolyFormGroup({});
		} else {

			updateOn = updateOn || 'blur';
			this.record ||= this.service.make()

			this.form = this.record.mergeIfUndefined(this._formDefaults).toFormGroup(this._formFields, undefined, updateOn);

			if (attachChangeNotifier) {
				this.attachChangeNotifiers();
				if (updateOn == 'blur') {
					// normally in a modal window there's a save button and the whole form is posted/updated at once
					//this.form.valueChanges.subscribe((_) => { this.onFormUpdate(_, this.service, this.form, this.record).subscribe(); });
					//} else {
					// in a standalone card, we monitor each field individually
					this.form.valueChanges.subscribe((_) => { this.onFieldUpdate(this.service, this.form, this.record).subscribe(); });
				} else {
					this.form.liveLoading = false;
				}
			}
		}

		if (loadingComplete) {
			this.markAsLoaded(this.form);
		}
	}

	protected updateForm(form?:TrolyFormGroup, record?:TrolyObject, defaults?:{}): void {

		form ||= this.form
		record ||= this.record
		defaults ||= this._formDefaults

		form.patchValue(record.mergeIfUndefined(defaults).patchableValues());
		form.markAsPristine()

	}

	/**
	 * Shorthand to indicate whether a loading indicator is activated
	 * @param key
	 * @returns
	 */
	public isLoading(key: string): boolean {
		return this.loading[key] && (this.loading[key] == 'loading' || this.loading[key] == 'refresh')
	}
	public isLoaded(key: string): boolean {
		return this.loading[key] && (this.loading[key] == 'loaded' || this.loading[key] == 'refresh');
	}
	public isLoadingError(key: string): boolean {
		return this.loading[key] && this.loading[key] == 'loading-error';
	}

	/**
	 *
	 * @param keys
	 */
	public markAsLoading(key?: string | string[] | TrolyFormGroup): boolean {

		if (typeof key == 'undefined') {
			Object.keys(this.loading).forEach((k) => {
				if (k != 'troly' && this.loading[k] == undefined) {
					this.loading[k] = 'loading';
				}
			}, this);
			return true;
		}

		return this.markAs(key, 'loading');
	}

	public notLoaded(key?: string | TrolyFormGroup): boolean {
		return (key instanceof TrolyFormGroup ? key.loading : this.loading[key]) == 'loading'
	}


	/**
	 *
	 * @param key
	 * @returns
	 */
	public markAsLoadingError(key: string | TrolyFormGroup, err?: any): boolean {
		if (err) {
			if (err.status == 401 || err.status == 403) {
				this.authService.logout();
			} else {
				this.logError(err);
			}
		}
		if (key instanceof TrolyFormGroup) {
			key.disable();
		}
		return this.markAs(key, 'loading-error');
	}

	public markAsLoadedTemporarily(key: string, keysToExclude: string[] = ['']): boolean {
		let currentValue = this.loading[key]
		setTimeout(() => {
			this.markAs(key, currentValue)
		}, 5000);
		return this.markAsLoaded(key, keysToExclude)
	}
	/**
	 *
	 * @param key
	 * @returns
	 */
	public markAsLoaded(key: string | TrolyFormGroup, keysToExclude: string[] = ['']): boolean {
		if (key instanceof String && keysToExclude.includes(key as string)) {
			return false
		}
		return this.markAs(key, 'loaded');
	}

	/**
	 * Marks the component form, or a loading indicator key, as being in a certain status
	 * @param key
	 * @param value
	 * @returns
	 */
	protected markAs(key: string | string[] | TrolyFormGroup, value: string): boolean {
		if (typeof key === 'string') {
			if (this.loading[key as string] != value) {
				this.loading[key as string] = value; return true
			}
		} else if (key instanceof Array) {
			key.map((k) => {
				this.loading[k] = value; return true
			})
		} else if (key instanceof TrolyFormGroup) {
			if (key.loading != value && ['loaded', 'loading', 'loading-error', 'loaded-demo'].includes(value)) {
				key.loading = value as 'loading' | 'loaded' | 'loading-error' | 'loaded-demo'; return true;
			}
		} else {
			this.logError(`Cannot mark form as ${value}: not a string or TrolyFormGroup.`);
		}
		return false;
	}

	/**
	 *
	 * @param form
	 */
	public enableFormNoChanges(form?: TrolyFormGroup) {
		form = form || this.form
		form.resetCodes({ error: 'NOCHANGE' });
		this.markAsLoaded(form);
		form.enable()

	}

	/**
	 * Extracts API generated errors when present on an object received.
	 * @param code the error code to register
	 * @param component  the component calling the function on this form, so that we can interact and set some error statuses
	 * @param loading name of the loading indicator to update.
	 */
	public unknownApiError(code: string, form?: TrolyFormGroup, loading?: string): void {
		form ||= this.form;
		
		form.disable();
		form.resetCodes({ error: 'UNKNOWN' }, [code]);

		this.markAsLoadingError(form);
		if (loading) { this.markAsLoadingError(loading); }
	}

	/**
	 *
	 * @param event
	 * @param service
	 * @param form
	 * @param o
	 * @returns
	 */
	public onFormUpdate<T extends TrolyObject>(service?: TrolyService<any>, form?: TrolyFormGroup, o?: TrolyObject, params?:{}): Observable<T> {

		service ||= this.service;
		o ||= this.record || service.record$.value || service.make();
		form ||= this.form

		this.markAsLoading(form);
		form.disable()

		return this.onFieldUpdate<T>(service, form, o, params).pipe(tap((_record) => {

			if (_record == null) {
				throw "A form was submitted with no information marked as dirty"
			}
			if (form.get('id') == undefined) {
				form.addControl('id', new FormControl(_record.id)); // allows success messages to provide a link to the record.
			}

			form.enable();
			this.markAsLoaded(form);

			//!!warning!! 
			//!! there may be cases where the subscribeRecord is attaching a customer, and yet, the form is editing / creating a different model
			this.record = _record
			//!! not sure about putting an if() here -- it might be an indicator that we shoud have a popup or a "simpler" formhandling component...?
		

			form.resetCodes({ success: o.id ? 'SAVED' : 'CREATED' });
			form.patchValue(_record.patchableValues());

		}), map((_record) => {
			if (!form.assignValidationErrors(_record)) { // checkObjectError sets errors and reenables the form.
				return _record
			}
		}), catchError((err) => {
			form.resetCodes({ error: 'UNKNOWN' });
			throw err
		}));
	}

	/**
	 * Retrieves changes made to a form an send update/save request to a specific service for an existing object.
	 * This is use as live-loading forms change detection, so doesn't automatically mark the whole form as loading.
	 * onFormUpdate() does that -- although both handle creating and saving objects
	 * @param event
	 * @param service
	 * @param form
	 * @param o
	 * @returns The updated object as received back from the service.save method.
	 */
	public onFieldUpdate<T extends TrolyObject>(service?: TrolyService<any>, form?: TrolyFormGroup, o?: TrolyObject, params?:{}): Observable<T> {
		
		service ||= this.service
		form ||= this.form
		o ||= this.record
		
		let changedProperties = form.getChanges();

		// there's no point in doing nothing if the form contains no changes
		if (form.pristine || form.status == 'INVALID') { return of(null); }
		//if (form.pristine) { return of(null); }


		// only save if field is dirty
		if (!changedProperties) {

			if (form.updateOn == 'change') {
				// forms which are submitted as a whole only -- normally popups
				this.enableFormNoChanges(form)
			}

		} else {

			let payload:T = service.make(changedProperties);

			this.logInfo(`== Saving ${o._trolyModelName} changes ==`);

			if (payload.beforeSave(o)) {
				
				form.startSaving(payload);

				return service.saveOrCreate(payload, params).pipe(tap((_record:TrolyObject) => {

					if (form.assignValidationErrors(_record)) {
						this.markAsLoaded(form);
					} else {
						_record = o.confirmChangesSaved(form, changedProperties, _record).postChangesSaved(_record); // we are receiving a TrolyObject from the service, but the method is declared by
					}

					this.updateConnectionSpeed(service.trolyConnection);

					// if record updated/received matches the service record name (which should be most times -- customerService saves customers, so the component is attached to a record as Customer)
					// then let's make sure -- as a convenience --  that the observer is updated.
					//service._next(_record) // ._next() ensures the id is matching and previously-set attributes are preserved.
					// the above line is not longer required -- it was added to the troly.service.save method on tap we push to ._next

				})) as Observable<T>

			} else {
				this.logInfo(`== Changes detected but filtered out by beforeSave()`);
			}
		}
		return of(null);

	}

	/**
	 * 
	 * @param changes 
	 * @returns 
	 */
	protected componentModifiedPayload(changes:{}, service:TrolyService<any>): TrolyObject {
		return service.make(changes);
	} 


	public onSubmit(): void { console.error(`operation not possible/implemented for ${this.__name}`); }

	public saveObject(record:TrolyObject, changes:{}, service?:TrolyService<any>, formStatusPath?:string): Observable<TrolyObject> {
		
		changes['id']= record.id
		service ||= this.service

		const savingStatus = new TrolyObject();
		if (this.form && formStatusPath) {
			savingStatus[formStatusPath] = true
			this.form.startSaving(savingStatus)
		}

		return new Observable<TrolyObject>((observer) => {
			/// update the status ans save
			service.save(service.make(changes)).subscribe({
				next: (_updated) => {
					if (this.form) { this.form.doneSaving(savingStatus); }
					observer.next(_updated)
				}, 
				error: (err) => {
					if (this.form) { this.form.savingError(savingStatus); }
					observer.error(err)
				},
				complete: () => { observer.complete() }
			})
		})
	}

	/**
	 *
	 * @param trolyConnection
	 */
	protected updateConnectionSpeed(trolyConnection: string) {
		this.loading.troly = trolyConnection != 'great' ? 'loading-slow' : 'loading';
	}

	/**
	 *
	 */
	public layoutAction(action: { [key: string]: any; }, e?:Event): boolean {
		if (e) { e.preventDefault(); e.stopPropagation(); }
		this.windowService.layoutActionNotifier.next(action);
		return false;
	}

	// 8888888b.                  888                  88888888888      d8b          888
	// 888   Y88b                 888                      888          Y8P          888
	// 888    888                 888                      888                       888
	// 888   d88P 8888b.  888d888 888888 888  888          888  888d888 888  .d8888b 888  888 .d8888b
	// 8888888P"     "88b 888P"   888    888  888          888  888P"   888 d88P"    888 .88P 88K
	// 888       .d888888 888     888    888  888          888  888     888 888      888888K  "Y8888b.
	// 888       888  888 888     Y88b.  Y88b 888          888  888     888 Y88b.    888 "88b      X88
	// 888       "Y888888 888      "Y888  "Y88888          888  888     888  "Y8888P 888  888  88888P'
	//                                        888
	//                                   Y8b d88P
	//                                    "Y88P"

	submitOnEnter($event) {
		if (this.form.valid && $event.srcElement.tagName != 'TEXTAREA' && $event.keyCode === 13) {
			$event.preventDefault();
			this.onSubmit();
		}
	}

	/**
	 * Returns a randomly selected element from an array.
	 * Used when many "placeholders" should be suggested, but we only want to a different one everytime the placeholder is displayed.
	 * @param items
	 * @returns
	 */
	public random(items: string[], method = 'random'): string {
		var index: number = 0;
		if (method != 'seconds') {

			if (items.length != 10) { console.error('random([],\'seconds\')) only supports arrays of 10 items'); }

			let s = (new Date).getSeconds()
			if (s < 10) { index = s; } else { index = parseInt(s.toString()[1]); }

		} else {
			index = Math.floor(Math.random() * items.length);
		}
		return items[index];
	}

	public shuffle = <T>(array: T[]) => {
		for (let i = array.length - 1; i > 0; i--) {
			const j = Math.floor(Math.random() * (i + 1));
			[array[i], array[j]] = [array[j], array[i]];
		}
		return array;
	};

	/**
	 * Handles click events from within tooltips to ensure the router is used (app not reloaded) for internal links.
	 * @param
	 */
	innerHtmlClickHandler(e) {

		const element: HTMLElement = e.target;
		if (element.nodeName === 'A') {
			e.preventDefault();
			let href = element.getAttribute('href');
			
			if (href[0] == '.' || href[0] == '/') { // only internal links
				let fragment = "";
				if (href.indexOf('#') > 0) {
					fragment = href.split('#')[1];
					href = href.split('#')[0];
				}
				this.router.navigate([href], { fragment: fragment });
			} else if (href.match(/crisp/)) {
				//return `<a href="javascript:$crisp.push(['do', 'helpdesk:article:open', ['en', '${slug}']]);">${text}</a>`
			}
		}
	}

	/**
	 *
	 * @param value
	 * @returns
	 */
	asDate(value?) { return (new Date(value)); }
	asFloat(value?) { return parseFloat(value || 0); }

	split(str: any, expr: string): string[] {
		if (str instanceof Array) { str = str.join(expr); }
		return (str && str.toString().split(expr)) || [];
	}

	protected get service(): TrolyService<any> { return super.service; }
	protected set service(value: TrolyService<any>) {
		super.service = value;
		if (!this.record) {
			// note we cannot call `this.service` because the "setter" method which we are overriding has not _finished running_
			this.record = super.service.make();
		}
	}

	// 888     888 8888888       .d8888b.           88888888888 d8b                   888b     d888  .d8888b.  8888888888
	// 888     888   888        d88P  "88b              888     Y8P                   8888b   d8888 d88P  Y88b 888
	// 888     888   888        Y88b. d88P              888                           88888b.d88888 888    888 888
	// 888     888   888         "Y8888P"               888     888 88888b.  888  888 888Y88888P888 888        8888888
	// 888     888   888        .d88P88K.d88P           888     888 888 "88b 888  888 888 Y888P 888 888        888
	// 888     888   888        888"  Y888P"            888     888 888  888 888  888 888  Y8P  888 888    888 888
	// Y88b. .d88P   888        Y88b .d8888b            888     888 888  888 Y88b 888 888   "   888 Y88b  d88P 888
	//  "Y88888P"  8888888       "Y8888P" Y88b          888     888 888  888  "Y88888 888       888  "Y8888P"  8888888888
	//                                                                            888
	//                                                                       Y8b d88P
	//                                                                        "Y88P"

	public toggleFromNow(event?: any): boolean {
		this.localCache.storeUiKey('date.is-relative.persist', !this.__fromNow);
		if (event) { event.preventDefault(); event.stopPropagation(); }
		return false; // allows to attach (click) on links without navigating away
	}

	scrollIntoView(id) {
		const element = document.getElementById(id);
		if (element) {
			element.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });
		}
	}

	protected clipboard: Clipboard = inject(Clipboard);
	public copyToClipboard(value: string, event?: Event) {

		this.clipboard.copy(value)
		if (event.target) {
			(<Element>event.target).classList?.add('bx-burst-once')
			setTimeout(() => {
				(<Element>event.target).classList?.remove('bx-burst-once')
			}, 2000)
		}
	}

	/**
	 * Returns the current darkmode Status taking into consideration current system preferences
	 * */
	public get darkModeActive(): boolean {
		return this.darkMode == 'auto' && window.matchMedia('(prefers-color-scheme: dark)').matches ? true : (this.darkMode == 'on' ? true : false)
	}
	public get darkModeImageTransformations(): {} {
		return this.darkModeActive ? { b:'rgb:3f494e' } : {}
	}

	/**
	 * Allows to override button definitions for tinyMCE.
	 * @param label 
	 * @param editor 
	 */
	protected tinyMCEButtonSetup(label, editor) { }
	/**
	 * Defines a Troly-default tinyMCE configuration for different size/purpose editors.
	 */
	protected tinyMCEConfig(size: 'full' | 'small' | 'mini' = 'mini', toolbar?: string, buttons?: string[]) {

		if (size == 'mini') {
			toolbar ||= 'undo redo'
		} else if (size == 'small') {
			toolbar ||= 'undo redo | bold italic | numlist bullist | link unlink | removeformat'
		} else if (size == 'full') {
			toolbar ||= 'undo redo | blocks | bold italic | outdent indent | alignleft aligncenter alignright alignjustify | numlist bullist | link unlink | image | removeformat'
			//const toolbar = 'undo redo | bold italic | outdent indent | link unlink | removeformat | template_tags'
		}

		if (buttons) {
			buttons.filter(_ => toolbar.indexOf(_) < 0).forEach((label, i) => {
				if (i == 0) { toolbar += ' | ';}
				toolbar += `${label} `;
			});
		}

		let parent = this; // when interacting with `this` object within a tinyMCE context, this refers to the editor, not the angular component.
		const height = size == 'full' ? 350 : (size == 'mini' ? 225 : 400)

		let plugins = toolbar.split(/[\|\s]/).map((item) => {
			// certain toolbar buttons need a plugin loaded -- if the button is requested, we load the plugin
			if (['link','image'].includes(item)) { return item; }
			if (['numlist','bullist'].includes(item)) { return 'lists'; }

			// plugins = ['advlist','autolink','lists','link','image','charmap','preview', 'searchreplace','visualblocks','code','fullscreen', 'table']
		}).filter((item) => item !== undefined)

		return {
			base_url: '/tinymce',
			suffix: '.min',

			branding: false,
			height: height,

			menubar: false,						// hides top menu -- https://www.tiny.cloud/docs/tinymce/latest/menus-configuration-options/#menubar
			statusbar: false,						// remove the status bar showing path -- https://www.tiny.cloud/docs/tinymce/latest/statusbar-configuration-options/#statusbar
			
			//content_style: "@font-face { font-family: Montserrat; src: url(/Montserrat-VariableFont_wght.ttf) format('truetype-variations'); font-weight: 200 800; font-style: normal; }; body { font-family: 'Montserrat', 'Helvetica Neue', sans-serif; color:red; font-size: 13px; };",
			content_style: "body { font-size: 14px; }",
			plugins: plugins,
			toolbar: toolbar,

			relative_urls: false,				// ensure we keep full urls (tasks and emails may be sent/exported) - https://www.tiny.cloud/docs/tinymce/latest/url-handling/

			paste_as_text: true,					// copy+paste clears formatting -- https://www.tiny.cloud/docs/tinymce/latest/copy-and-paste/#paste_as_text
			paste_block_drop: false,			// drag and drop clears formatting -- https://www.tiny.cloud/docs/tinymce/latest/copy-and-paste/#paste_block_drop
			paste_data_images: false,			// drag and drop removes images -- https://www.tiny.cloud/docs/tinymce/latest/copy-and-paste/#paste_data_images

			block_formats: 'Body text=p;Large Title=h1;Small Title=h3', // https://www.tiny.cloud/docs/tinymce/latest/user-formatting-options/#block_formats

			skin: this.darkModeActive ? 'tinymce-5-dark' : 'tinymce-5',
			content_css: this.darkModeActive ? 'tinymce-5-dark' : 'tinymce-5',
			
			license_key: 'gpl',
			text_patterns: [], 					// disable auto formatting of patterns -- (see https://www.tiny.cloud/docs/tinymce/latest/content-behavior-options/#text_patterns)

			setup: function (editor) {
				/* hide other options until the editor has initialised */
				editor.on('init', function (e) { parent.markAsLoaded('editor') });

				if (buttons && buttons.length > 0) {
					buttons.forEach((label, i) => {
						editor.ui.registry.addMenuButton(label, parent.tinyMCEButtonSetup(label, editor));
					})
				}
			},
		};
	}

	public intercomArticleLink(text: string, slug: string) {
		return `<a href="javascript:Intercom('showArticle', ${slug}); return false;">${text}</a>`;
	}
	public crispHelpdeskLink(text: string, slug: string) {
		return `<a href="javascript:$crisp.push(['do', 'helpdesk:article:open', ['en', '${slug}']]);">${text}</a>`;
	}

	crispHelpdeskPopup() { }

	public alertOptions(level: string, confirmText: string, cancelText?: string) {

		let actionClass = 'btn-dark';
		switch (level) {
			case 'info': 		actionClass = 'btn-secondary'; 	break;
			case 'question': 	actionClass = 'btn-primary'; 		break;
			case 'error': 		actionClass = 'btn-warning'; 		break;
			case 'warning':	actionClass = 'btn-danger'; 		break;
		}

		let options = {
			icon: level as SweetAlertOptions['icon'],
			confirmButtonText: confirmText,
			customClass: {
				confirmButton: `btn ${actionClass}`,
				cancelButton: 'btn btn-light',
			},
			buttonsStyling: false,
			position: 'top' as SweetAlertPosition
		};

		if (cancelText) {
			options['cancelButtonText'] = cancelText;
			options['showCancelButton'] = true;
			options['reverseButtons'] = true;
		}

		return options;
	}

	//  .d8888b.           888                   888    d8b                        d8b                      d8888
	// d88P  Y88b          888                   888    Y8P                        Y8P                     d88888
	// Y88b.               888                   888                                                      d88P888
	//  "Y888b.    .d88b.  888  .d88b.   .d8888b 888888 888  .d88b.  88888b.       888 88888b.           d88P 888 888d888 888d888 8888b.  888  888
	//     "Y88b. d8P  Y8b 888 d8P  Y8b d88P"    888    888 d88""88b 888 "88b      888 888 "88b         d88P  888 888P"   888P"      "88b 888  888
	//       "888 88888888 888 88888888 888      888    888 888  888 888  888      888 888  888        d88P   888 888     888    .d888888 888  888
	// Y88b  d88P Y8b.     888 Y8b.     Y88b.    Y88b.  888 Y88..88P 888  888      888 888  888       d8888888888 888     888    888  888 Y88b 888
	//  "Y8888P"   "Y8888  888  "Y8888   "Y8888P  "Y888 888  "Y88P"  888  888      888 888  888      d88P     888 888     888    "Y888888  "Y88888
	//                                                                                                                                         888
	//                                                                                                                                    Y8b d88P
	//                                                                                                                                     "Y88P"


	/**
	 * Called when a new object needs to be added to a form array of objects.
	 * @param attrs attributes already defined or set for the new object
	 * @param arrayName form array name we're trying to create a new object for
	 */
	protected createArrayElement(attrs:{}, arrayName:string): TrolyObject {

		let newElement = new TrolyObject('', attrs);

		// make sure there's a unique ID
		newElement.id ||= this.service.genUUID();
		while (this.form.get(arrayName).value.find((v:any) => v.id == newElement.id)) {
			newElement.id = this.service.genUUID()
		}

		return newElement;
	}

	/**
	 * Adds a new element to a FormArray, ensuring there's a unique ID attached.
	 * @param attrs object with attributes to become the form group, if an ID is not provided, it will be added
	 * @param arrayName the name to reach the correct formArray
	 * @param i:number the index to insert the new element at, if not provided, it will be added to the end, use -1 to add to the beginning
	 */
	protected addArrayElement(attrs:string|{}|TrolyObject, arrayName:string, i:number = null) {

		if (typeof attrs == 'string') { attrs = {name:attrs} }

		if (!(attrs instanceof TrolyObject)) {
			attrs = this.createArrayElement(attrs, arrayName);
			if (attrs == null) return; // if the 'createArrayElement' has elected we should not add a new element (eg, we have reached the max number of elements...), then exit
		}

		// At this point we have handled the attrs param as string|{}|TrolyObject,
		// so we can safely continue cast it to a TrolyObject to work with
		const newElement:TrolyObject = attrs as TrolyObject;

		// By default, Angular only markAsDirty when the interaction comes from a user
		// here we are programatically adding elements; so we find out where the new goes, insert it,
		// and loop through the element attributes to mark them as dirty -- which is bubbled up to the formArray and form itself.
		i ||= this.form.array(arrayName).length;
		this.form.array(arrayName).insert(i, newElement.toFormGroup(newElement._formFields));
		if (i == -1) { i = 0; /** inserting at the top of the array means the element is now at index 0 */}
		attrs = newElement.cleanObjectProperties(newElement);
		Object.keys(attrs).forEach((key) => {
			const val = this.form.get(`${arrayName}.${i}.${key}`).value.toString();
			// we only mark as dirty if:
			if ((key == 'id' && Object.keys(attrs).length == 1) 									// we ONLY have an ID attribute (checkboxes to select IDs, etc) -- deals with warehouse ids set as id when selected
				|| (key != 'id' && val != '') 				 											// OR we're looking at a non-empty value, and it's not the id (which is auto-generated) -- deals with text boxes with no value
				) {
					// ? remember the markAsDirty() is bulled up so the index, and the arrayName both get set as dirty
					//this.form.get(`${arrayName}.${i}.id`).markAsDirty();		// possibly setting this numerous times, but who cares, code is cleaner
					//this.form.get(`${arrayName}.${i}.${key}`).markAsDirty(); // possible that key == id, but who cares, code is cleaner
				}
		});

		return true
	}

	/**
	 * Allows to set / change the strategy used to mark elements as dirty - either the whole object is dirty (eg when the API requires the whole object sent back) or only individual attributes (when the API can merge objects -- eg see Listable)
	 * ! note -- this can from the fact that angular doesn't maintain the dirty status of a formGroup when the FormArray it's in changes.
	 * @param strategy, to employ, defaults to 'all' because it generally less error prone
	 */
	protected set arrayElementAllDirtyStrategy(arrayName:string) {
		if (!this._arrayElementDirtyStrategy.includes(arrayName)) {

			this.form.get(arrayName).valueChanges
				.pipe(pairwise())
				.subscribe(([prev, next]: [any, any]) => {
					// see pairwise usage notes here: https://stackoverflow.com/questions/44898010/form-control-valuechanges-gives-the-previous-value
					if (prev.length == next.length) { return; } // ignore the internal changes, and focus only in the array changes
					this.form.array(arrayName).controls.forEach((_, i) => {
						Object.keys(this.form.get(`${arrayName}.${i}`).value).forEach((key) => {
							this.form.get(`${arrayName}.${i}.${key}`).markAsDirty();
						})
					});
				})

			this._arrayElementDirtyStrategy.push(arrayName);
		}
	}
	private _arrayElementDirtyStrategy: string[] = [];

	/**
	 * Changes the visibility of the element capture section in a form, after
	 * validating that it can be shown/hidden
	 **/
	public toggleArrayElements(value:boolean, arrayName:string, newValue?:string):boolean {

		if (value) {
			//if (this.form.array(arrayName).length == 0 && newValue !== undefined) {
			if (newValue !== undefined) {
				this.addArrayElement(newValue, arrayName)
			}
		} else {
			switch (this.form.array(arrayName).length) {
				case 0:
					break; // if we have no elements in the array, and we've been asked to turn if off, then we're ok with that.
				case 1:
					if (newValue !== undefined) {
						// the default placeholder (sometimes empty) was created and should also be removed
						this.removeArrayElement(0, arrayName)
						this.form.get(arrayName).setValue([]);
					} else {
						// we have been asked to toggle off, but there's IS an element that shouldn't be removed
						value = true
					}
					break;
				default:
					// if there's more than one variation, then we can't toggle off
					value = true
			}
		}
		return value;
	}

	/**
	 *
	 * @param value
	 * @param status
	 * @param arrayName
	 * @returns
	 */
	public toggleArrayElement(value:string, status:boolean, arrayName:string):boolean {
		if (status) { return this.addArrayElement(value, arrayName); }
		else {  return this.removeArrayElement(value, arrayName); }
	}

	/**
	 * Adds a new 'placeholder' element when the user starts typing, if there's a value entered
	 */
	public conditionallyAddArrayElement(currentValue:string, i: number, arrayName:string) {
		if (currentValue) {
			// we have input, add another control if we don't have a next one
			if (this.form.array(arrayName).length == (i+1)) {
				this.addArrayElement('', arrayName)
			}
		}
	}

	/** Removes the variation at the specified index */
	public removeArrayElement(i: string|number=0, arrayName:string):boolean { /** we have string in the definition because i can be the element identifier (when overriden) or the actual index to remove */
		if (typeof i == 'string') { i = parseInt(i) }
		if (i < this.form.array(arrayName).length) {
			(this.form.get(arrayName) as FormArray).removeAt(i)
			// much like addArrayElement, we need to mark the form as dirty after removing an element, because
			// the standard behaviour of Angular is to only markAsDirty when the user interacts with the form
			this.form.get(arrayName).markAsDirty();
			return true
		}
		return false
	}

	/**
	 * Determines whether we should remove the next (empty) element of an array (tru) or the current one (false)
	 * When adding/removing fields on keystroke, removing the next (empty) allows to keep the focus on the current form field
	 * @param i 
	 * @param arrayName 
	 * @returns 
	 */
	public removeNextArrayElement(i: number, arrayName:string):boolean {
		return this.form.get(`${arrayName}.${i+1}`).value['name'] == ''
	}

	/** Removes the variation at the specified index but ONLY if it's empty */
	public conditionallyRemoveArrayElement(event:any, i: number, arrayName:string) {
		if (event.target.value == '') {
			// we don't have a name, remove this control if we have a next one
			if (this.form.array(arrayName).length > (i+1)) {
				if (this.removeNextArrayElement(i, arrayName)) {
					this.removeArrayElement(i+1, arrayName);
				} else {
					this.removeArrayElement(i, arrayName);
				}
			}
		}
	}

	public isArrayElementSelected(value:{}, arrayName:string|any[]):boolean {
		const key = Object.keys(value)[0];
		const collection = typeof arrayName == 'string' ? this.form.array(arrayName).controls.map(_ => _.value) : arrayName;
		return collection.findIndex(_ => _[key] == value[key]) > -1;
	}
}